import React, { Suspense, useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import ReactGA from "react-ga";
import Signin from './pages/Signin/Signin';
import Signup from './pages/Signup/Signup';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import VendorList from './pages/VendorList/VendorList';
import Preloader from './Preloader';
import AOS from 'aos';
import { PrimeReactProvider } from 'primereact/api';
import "primereact/resources/themes/bootstrap4-light-purple/theme.css";
import ProtectedRoute from './ProtectedRoute';
import AdminLogin from './admin/AdminLogin/AdminLogin';
import Layout from './admin/AdminLayout/Layout';

const Home = React.lazy(() => import('./pages/Home/Home'));
const AboutUs = React.lazy(() => import('./pages/AboutUs/AboutUs'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy/PrivacyPolicy'));
const TermsAndConditions = React.lazy(() => import('./pages/TermsAndConditions/TermsAndConditions'));
const FaQ = React.lazy(() => import('./pages/FaQ/FaQ'));
const ContactUs = React.lazy(() => import('./pages/ContactUs/ContactUs'));
const Services = React.lazy(() => import('./pages/Services/Services'));
const Booking = React.lazy(() => import('./pages/Booking/Booking'));
const Dashboard = React.lazy(() => import('./pages/Dashboard/Dashboard'));
const ChangePassword = React.lazy(() => import('./pages/ChangePassword/ChangePassword'));
const AdminDashboard = React.lazy(() => import('./admin/AdminDashboard/AdminDashboard'));
const Reservation = React.lazy(() => import('./admin/Reservation/Reservation'));
const Bookings = React.lazy(() => import('./admin/Bookings/Bookings'));
const Users = React.lazy(() => import('./admin/Users/Users'));
const Customers = React.lazy(() => import('./admin/Customers/Customers'));
const VendorsTerminal = React.lazy(() => import('./admin/VendorsTerminal/VendorTerminal'));
const ServiceType = React.lazy(() => import('./admin/ServiceType/ServiceType'));
const Vendors = React.lazy(() => import('./admin/Vendors/Vendors'));
const Airports = React.lazy(() => import('./admin/Airports/Airports'));
const VendorsBookings = React.lazy(() => import('./admin/Vendors/VendorBookings'));
const Extras = React.lazy(() => import("./admin/Extras/Extras"));
const AirportLanding = React.lazy(() => import('./pages/AirportLanding/AirportLanding'));

const MEASUREMENT_ID = process.env.REACT_APP_MEASUREMENT_ID;
ReactGA.initialize(MEASUREMENT_ID);

/* ── Live booking notifications ── */
const BOOKING_MSGS = [
  'Someone in London just booked Luton Meet & Greet',
  'A customer in Birmingham booked Heathrow parking',
  'Someone in Manchester booked Luton Park & Ride',
  'A customer just used PLATINUM10 and saved £18',
  'Someone in Leeds booked Heathrow Meet & Greet',
  'A family in Bristol just booked 14 days parking',
  'A customer in Sheffield booked Gatwick parking',
  'Someone in Edinburgh booked Manchester Meet & Greet',
];

function LiveBookingNotification() {
  const [visible, setVisible] = useState(false);
  const [msgIndex, setMsgIndex] = useState(0);

  useEffect(() => {
    const show = () => {
      setMsgIndex(i => (i + 1) % BOOKING_MSGS.length);
      setVisible(true);
      setTimeout(() => setVisible(false), 4500);
    };
    const timer = setTimeout(() => {
      show();
      const interval = setInterval(show, 28000);
      return () => clearInterval(interval);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  return (
    <div style={{
      position:'fixed', bottom:'90px', left:'20px', zIndex:9998,
      background:'#fff', border:'1px solid #e3eaf0',
      borderLeft:'4px solid #667eea', borderRadius:'12px',
      padding:'12px 16px', fontFamily:'Inter,sans-serif',
      fontSize:'11px', fontWeight:'500', color:'#374151',
      boxShadow:'0 8px 24px rgba(0,0,0,0.12)',
      maxWidth:'280px', display:'flex', alignItems:'center', gap:'10px',
      animation:'slideInLeft 0.4s cubic-bezier(0.34,1.56,0.64,1)',
    }}>
      <style>{`
        @keyframes slideInLeft{from{transform:translateX(-110%);opacity:0}to{transform:translateX(0);opacity:1}}
        @keyframes waPulse{0%{box-shadow:0 4px 20px rgba(37,211,102,.5)}50%{box-shadow:0 4px 30px rgba(37,211,102,.8),0 0 0 10px rgba(37,211,102,.1)}100%{box-shadow:0 4px 20px rgba(37,211,102,.5)}}
        @keyframes pulseRed{0%,100%{opacity:1}50%{opacity:.3}}
      `}</style>
      <div style={{width:'32px',height:'32px',borderRadius:'50%',background:'linear-gradient(135deg,#667eea,#764ba2)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
      </div>
      <div>
        <div style={{color:'#374151',fontWeight:600,fontSize:'11px'}}>{BOOKING_MSGS[msgIndex]}</div>
        <div style={{color:'#9ca3af',fontSize:'9px',marginTop:'2px'}}>Just now · platinumholidayservice.co.uk</div>
      </div>
    </div>
  );
}

/* ── Exit Intent Popup ── */
function ExitIntentPopup() {
  const [show, setShow] = useState(false);
  const [seconds, setSeconds] = useState(600);
  const location = useLocation();
  const isAdmin = location.pathname.includes('admin') || location.pathname.includes('booking');

  useEffect(() => {
    if (isAdmin) return;
    if (sessionStorage.getItem('ph-exit-shown')) return;

    const handleMouseLeave = (e) => {
      if (e.clientY < 10) {
        setShow(true);
        sessionStorage.setItem('ph-exit-shown', '1');
      }
    };
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [isAdmin]);

  useEffect(() => {
    if (!show) return;
    const t = setInterval(() => setSeconds(s => s > 0 ? s - 1 : 0), 1000);
    return () => clearInterval(t);
  }, [show]);

  if (!show) return null;

  const m = Math.floor(seconds / 60);
  const s = seconds % 60;

  return (
    <div onClick={(e) => e.target === e.currentTarget && setShow(false)} style={{
      position:'fixed',inset:0,background:'rgba(0,0,0,0.72)',zIndex:99999,
      display:'flex',alignItems:'center',justifyContent:'center',padding:'20px',
      animation:'fadeIn .3s ease',
    }}>
      <style>{`@keyframes fadeIn{from{opacity:0}to{opacity:1}}@keyframes popIn{from{opacity:0;transform:scale(.92)}to{opacity:1;transform:scale(1)}}`}</style>
      <div style={{
        background:'#fff',borderRadius:'24px',padding:'36px',
        maxWidth:'420px',width:'100%',textAlign:'center',position:'relative',
        boxShadow:'0 32px 80px rgba(0,0,0,0.3)',overflow:'hidden',
        animation:'popIn .35s cubic-bezier(0.34,1.56,0.64,1)',
      }}>
        <div style={{position:'absolute',top:0,left:0,right:0,height:'4px',background:'linear-gradient(135deg,#667eea,#764ba2)'}}/>
        <button onClick={() => setShow(false)} style={{position:'absolute',top:'14px',right:'16px',background:'none',border:'none',fontSize:'22px',cursor:'pointer',color:'#9ca3af',lineHeight:1}}>×</button>
        <div style={{width:'60px',height:'60px',borderRadius:'50%',background:'linear-gradient(135deg,#667eea,#764ba2)',display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 18px',fontSize:'26px',boxShadow:'0 4px 24px rgba(102,126,234,.35)'}}>✈</div>
        <h2 style={{fontFamily:'Inter,sans-serif',fontSize:'22px',fontWeight:900,color:'#0d1b2a',letterSpacing:'-.025em',marginBottom:'8px'}}>Your space is still available!</h2>
        <p style={{fontFamily:'Inter,sans-serif',fontSize:'13px',color:'#6b7280',marginBottom:'20px',lineHeight:1.65}}>Don't miss out. Lock in today's price with your exclusive discount before it expires.</p>
        <div style={{background:'linear-gradient(135deg,rgba(102,126,234,.07),rgba(118,75,162,.04))',border:'2px dashed #667eea',borderRadius:'14px',padding:'16px',marginBottom:'20px'}}>
          <div style={{fontFamily:'Inter,sans-serif',fontSize:'10px',fontWeight:700,color:'#9ca3af',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:'6px'}}>Your exclusive discount code</div>
          <div style={{fontFamily:'Inter,sans-serif',fontSize:'30px',fontWeight:900,color:'#667eea',letterSpacing:'3px'}}>PLATINUM10</div>
          <div style={{fontFamily:'Inter,sans-serif',fontSize:'12px',color:'#10b981',fontWeight:600,marginTop:'5px'}}>
            10% off — expires in {m}:{s.toString().padStart(2,'0')}
          </div>
        </div>
        <button onClick={() => setShow(false)} style={{width:'100%',padding:'14px',background:'linear-gradient(135deg,#667eea,#764ba2)',border:'none',borderRadius:'12px',color:'#fff',fontFamily:'Inter,sans-serif',fontSize:'14px',fontWeight:700,cursor:'pointer',boxShadow:'0 4px 24px rgba(102,126,234,.35)',marginBottom:'10px'}}>
          ✈ Complete My Booking
        </button>
        <button onClick={() => setShow(false)} style={{background:'none',border:'none',fontFamily:'Inter,sans-serif',fontSize:'11px',color:'#9ca3af',cursor:'pointer'}}>
          No thanks, I'll pay full price
        </button>
      </div>
    </div>
  );
}

/* ── Social Proof Strip ── */
function SocialProofStrip() {
  const location = useLocation();
  const isAdmin = location.pathname.includes('admin') || location.pathname.includes('dashboard') || location.pathname.includes('bookings');
  if (isAdmin) return null;

  return (
    <div style={{
      background:'#f0f9ff',borderBottom:'1px solid #e0f2fe',
      padding:'7px 20px',display:'flex',alignItems:'center',
      justifyContent:'center',gap:'20px',flexWrap:'wrap',
      fontFamily:'Inter,sans-serif',fontSize:'11px',fontWeight:600,color:'#374151',
      position:'sticky',top:'34px',zIndex:197,
    }}>
      <span style={{display:'flex',alignItems:'center',gap:'5px'}}><span style={{color:'#10b981'}}>✅</span> 47 bookings today</span>
      <span style={{color:'#e0f2fe'}}>|</span>
      <span style={{display:'flex',alignItems:'center',gap:'5px'}}><span style={{color:'#f59e0b'}}>⭐</span> 4.9/5 from 127 reviews</span>
      <span style={{color:'#e0f2fe'}}>|</span>
      <span style={{display:'flex',alignItems:'center',gap:'5px'}}><span style={{color:'#667eea'}}>🔒</span> Secure Stripe payments</span>
      <span style={{color:'#e0f2fe'}}>|</span>
      <span style={{display:'flex',alignItems:'center',gap:'5px'}}><span style={{color:'#10b981'}}>✅</span> Free cancellation</span>
    </div>
  );
}

function App() {
  const value = { ripple: true };

  useEffect(() => {
    AOS.init({ duration: 1000, easing: "ease-in-out", once: true, mirror: false });
  }, []);

  return (
    <PrimeReactProvider value={value}>

      {/* Launch Banner */}
      <div style={{background:"linear-gradient(135deg,#667eea,#764ba2)",color:"#fff",textAlign:"center",padding:"7px 12px",fontSize:"12px",fontWeight:"600",position:"fixed",top:"0",left:"0",right:"0",zIndex:"9999",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",letterSpacing:".01em"}}>
        🎉 Use code <span style={{background:"rgba(255,255,255,0.22)",padding:"2px 8px",borderRadius:"5px",margin:"0 5px",fontWeight:700,letterSpacing:'.5px'}}>PLATINUM10</span> for 10% off your first booking!
      </div>

      {/* Social Proof Strip */}
      <SocialProofStrip />

      {/* Live Booking Notifications */}
      <LiveBookingNotification />

      {/* Exit Intent Popup */}
      <ExitIntentPopup />

      {/* WhatsApp Button */}
      <a
        href="https://wa.me/447375551666?text=Hi%2C%20I%20would%20like%20to%20book%20airport%20parking%20at%20Platinum%20Holiday%20Service"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat with us on WhatsApp"
        style={{position:"fixed",bottom:"24px",right:"24px",zIndex:"9999",width:"56px",height:"56px",borderRadius:"50%",background:"#25D366",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",textDecoration:"none",animation:"waPulse 2s infinite"}}
      >
        <svg viewBox="0 0 24 24" width="28" height="28" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      <Routes>
        {/* Main site */}
        <Route path='/' element={<Suspense fallback={<Preloader />}><ProtectedRoute><Home /></ProtectedRoute></Suspense>} />
        <Route path='/about-us' element={<Suspense fallback={<Preloader />}><ProtectedRoute><AboutUs /></ProtectedRoute></Suspense>} />
        <Route path='/sign-in' element={<Signin />} />
        <Route path='/sign-up' element={<Signup />} />
        <Route path='/forgot-password' element={<ForgotPassword />} />
        <Route path='/privacy-policy' element={<Suspense fallback={<Preloader />}><ProtectedRoute><PrivacyPolicy /></ProtectedRoute></Suspense>} />
        <Route path='/terms-and-conditions' element={<Suspense fallback={<Preloader />}><ProtectedRoute><TermsAndConditions /></ProtectedRoute></Suspense>} />
        <Route path='/faq' element={<Suspense fallback={<Preloader />}><ProtectedRoute><FaQ /></ProtectedRoute></Suspense>} />
        <Route path='/contact-us' element={<Suspense fallback={<Preloader />}><ProtectedRoute><ContactUs /></ProtectedRoute></Suspense>} />
        <Route path='/services' element={<Suspense fallback={<Preloader />}><ProtectedRoute><Services /></ProtectedRoute></Suspense>} />
        <Route path='/results' element={<Suspense fallback={<Preloader />}><VendorList /></Suspense>} />
        <Route path='/booking' element={<Suspense fallback={<Preloader />}><Booking /></Suspense>} />
        <Route path='/dashboard' element={<Suspense fallback={<Preloader />}><ProtectedRoute><Dashboard /></ProtectedRoute></Suspense>} />
        <Route path='/change-password' element={<Suspense fallback={<Preloader />}><ProtectedRoute><ChangePassword /></ProtectedRoute></Suspense>} />

        {/* Airport landing pages — UK */}
        <Route path='/luton-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="luton" /></Suspense>} />
        <Route path='/heathrow-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="heathrow" /></Suspense>} />
        <Route path='/gatwick-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="gatwick" /></Suspense>} />
        <Route path='/manchester-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="manchester" /></Suspense>} />
        <Route path='/stansted-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="stansted" /></Suspense>} />
        <Route path='/birmingham-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="birmingham" /></Suspense>} />
        <Route path='/edinburgh-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="edinburgh" /></Suspense>} />
        <Route path='/bristol-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="bristol" /></Suspense>} />
        <Route path='/glasgow-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="glasgow" /></Suspense>} />
        <Route path='/leeds-bradford-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="leeds-bradford" /></Suspense>} />
        <Route path='/newcastle-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="newcastle" /></Suspense>} />
        <Route path='/belfast-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="belfast" /></Suspense>} />
        <Route path='/liverpool-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="liverpool" /></Suspense>} />
        <Route path='/east-midlands-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="east-midlands" /></Suspense>} />
        <Route path='/cardiff-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="cardiff" /></Suspense>} />
        <Route path='/southampton-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="southampton" /></Suspense>} />
        <Route path='/aberdeen-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="aberdeen" /></Suspense>} />
        <Route path='/exeter-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="exeter" /></Suspense>} />
        <Route path='/london-city-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="london-city" /></Suspense>} />
        <Route path='/doncaster-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="doncaster" /></Suspense>} />
        <Route path='/norwich-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="norwich" /></Suspense>} />
        <Route path='/inverness-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="inverness" /></Suspense>} />
        <Route path='/bournemouth-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="bournemouth" /></Suspense>} />
        <Route path='/prestwick-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="prestwick" /></Suspense>} />
        <Route path='/teesside-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="teesside" /></Suspense>} />
        <Route path='/jersey-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="jersey" /></Suspense>} />
        <Route path='/isle-of-man-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="isle-of-man" /></Suspense>} />
        <Route path='/newquay-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="newquay" /></Suspense>} />
        <Route path='/humberside-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="humberside" /></Suspense>} />
        <Route path='/belfast-city-airport-parking' element={<Suspense fallback={<Preloader />}><AirportLanding airport="belfast-city" /></Suspense>} />

        {/* Service-specific pages */}
        <Route path='/meet-and-greet-luton' element={<Suspense fallback={<Preloader />}><AirportLanding airport="luton" service="meet-and-greet" /></Suspense>} />
        <Route path='/park-and-ride-luton' element={<Suspense fallback={<Preloader />}><AirportLanding airport="luton" service="park-and-ride" /></Suspense>} />
        <Route path='/meet-and-greet-heathrow' element={<Suspense fallback={<Preloader />}><AirportLanding airport="heathrow" service="meet-and-greet" /></Suspense>} />
        <Route path='/park-and-ride-heathrow' element={<Suspense fallback={<Preloader />}><AirportLanding airport="heathrow" service="park-and-ride" /></Suspense>} />

        {/* Admin */}
        <Route path='/admin-login' element={<ProtectedRoute><AdminLogin /></ProtectedRoute>} />
        <Route path="/" element={<Layout />}>
          <Route path='admin-dashboard' element={<Suspense fallback={<Preloader />}><ProtectedRoute><AdminDashboard /></ProtectedRoute></Suspense>} />
          <Route path='reservation' element={<Suspense fallback={<Preloader />}><ProtectedRoute><Reservation /></ProtectedRoute></Suspense>} />
          <Route path='bookings' element={<Suspense fallback={<Preloader />}><ProtectedRoute><Bookings /></ProtectedRoute></Suspense>} />
          <Route path='users' element={<Suspense fallback={<Preloader />}><ProtectedRoute><Users /></ProtectedRoute></Suspense>} />
          <Route path='customers' element={<Suspense fallback={<Preloader />}><ProtectedRoute><Customers /></ProtectedRoute></Suspense>} />
          <Route path='vendors' element={<Suspense fallback={<Preloader />}><ProtectedRoute><Vendors /></ProtectedRoute></Suspense>} />
          <Route path='vendorsTerminal' element={<Suspense fallback={<Preloader />}><ProtectedRoute><VendorsTerminal /></ProtectedRoute></Suspense>} />
          <Route path='serviceType' element={<Suspense fallback={<Preloader />}><ProtectedRoute><ServiceType /></ProtectedRoute></Suspense>} />
          <Route path='airports' element={<Suspense fallback={<Preloader />}><ProtectedRoute><Airports /></ProtectedRoute></Suspense>} />
          <Route path='vendors/bookings/:id' element={<Suspense fallback={<Preloader />}><ProtectedRoute><VendorsBookings /></ProtectedRoute></Suspense>} />
          <Route path='extras' element={<Suspense fallback={<Preloader />}><ProtectedRoute><Extras /></ProtectedRoute></Suspense>} />
        </Route>
      </Routes>
    </PrimeReactProvider>
  );
}

export default App;
